/**
 * Schema.org JSON-LD builders for SEO.
 * These structures power Google rich results, Google for Jobs listings, and other
 * search-engine features. Validate any change against Google's Rich Results Test.
 */

import type { Job } from '../types';

const SITE_URL = (() => {
  const fromEnv = process.env.REACT_APP_PUBLIC_BASE_URL;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  if (typeof window !== 'undefined') return window.location.origin;
  return 'https://talixjobs.com';
})();

const EMPLOYMENT_TYPE_MAP: Record<string, string> = {
  'full-time': 'FULL_TIME',
  'part-time': 'PART_TIME',
  contract: 'CONTRACTOR',
  internship: 'INTERN',
  temporary: 'TEMPORARY',
  volunteer: 'VOLUNTEER',
};

function plainText(html: string | undefined | null, max = 1600): string {
  if (!html) return '';
  const txt = html
    .replace(/<\/?[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return txt.length > max ? `${txt.slice(0, max - 1).trimEnd()}…` : txt;
}

/** Build Schema.org JobPosting structured data for the Job detail page. */
export function buildJobPostingJsonLd(job: Job, canonicalUrl: string): object {
  const employmentType = EMPLOYMENT_TYPE_MAP[(job.job_type || '').toLowerCase()];
  const datePosted = job.created_at ? new Date(job.created_at).toISOString().slice(0, 10) : undefined;
  const validThrough = job.expires_at ? new Date(job.expires_at).toISOString() : undefined;

  const hiringOrg: any = { '@type': 'Organization' };
  if (job.company?.name) hiringOrg.name = job.company.name;
  if (job.company?.website) hiringOrg.sameAs = job.company.website;
  if (job.company?.logo_url) hiringOrg.logo = job.company.logo_url;

  const jobLocation: any = job.location
    ? {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: job.location,
          addressCountry: 'IN',
        },
      }
    : undefined;

  const baseSalary =
    job.salary_min || job.salary_max
      ? {
          '@type': 'MonetaryAmount',
          currency: job.salary_currency || 'INR',
          value: {
            '@type': 'QuantitativeValue',
            minValue: job.salary_min ?? undefined,
            maxValue: job.salary_max ?? undefined,
            unitText: 'YEAR',
          },
        }
      : undefined;

  const jsonLd: any = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: job.title,
    description: plainText(job.description, 5000),
    identifier: { '@type': 'PropertyValue', name: 'talixjobs', value: String(job.id) },
    url: canonicalUrl,
    hiringOrganization: hiringOrg,
    directApply: true,
  };

  if (datePosted) jsonLd.datePosted = datePosted;
  if (validThrough) jsonLd.validThrough = validThrough;
  if (employmentType) jsonLd.employmentType = employmentType;
  if (jobLocation) jsonLd.jobLocation = jobLocation;
  if (baseSalary) jsonLd.baseSalary = baseSalary;
  if (job.remote_allowed) {
    jsonLd.jobLocationType = 'TELECOMMUTE';
    jsonLd.applicantLocationRequirements = { '@type': 'Country', name: 'IN' };
  }
  const minMonths = Math.max(0, (Number(job.min_experience_years) || 0) * 12 + (Number(job.min_experience_months) || 0));
  if (minMonths > 0) {
    jsonLd.experienceRequirements = { '@type': 'OccupationalExperienceRequirements', monthsOfExperience: minMonths };
  }

  return jsonLd;
}

/** Build Schema.org WebSite + Organization combined JSON-LD for the homepage. */
export function buildSiteJsonLd(): object[] {
  return [
    {
      '@context': 'https://schema.org/',
      '@type': 'WebSite',
      name: 'talixjobs',
      url: SITE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/jobs?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org/',
      '@type': 'Organization',
      name: 'talixjobs',
      url: SITE_URL,
      logo: `${SITE_URL}/talix-jobs-logo.jpg`,
    },
  ];
}

/** Build Schema.org ItemList JSON-LD for a job listings page (Browse Jobs). */
export function buildJobListJsonLd(jobs: Job[]): object {
  return {
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    itemListElement: jobs.slice(0, 20).map((job, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE_URL}/jobs/${buildJobCanonicalSlug(job)}`,
      name: job.title,
    })),
  };
}

function buildJobCanonicalSlug(job: Job): string {
  // Lazy avoid circular import: rebuild a minimal slug. Centralized version lives in utils/slug.
  // We keep this duplicate small and deliberately simple — it's only the URL form.
  const parts = [job.title];
  if (job.company?.name) parts.push(`at ${job.company.name}`);
  if (job.location) parts.push(`in ${job.location}`);
  const slug = parts
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/ /g, '-')
    .slice(0, 70);
  return `${slug}-${job.id}`;
}
